<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { UpstreamFormatDetail, UpstreamFormatKind, UpstreamFormatSummary } from '@ucc/shared';
  import {
    ApiError,
    createUpstreamFormat,
    deleteUpstreamFormat,
    fetchUpstreamFormat,
    fetchUpstreamFormats,
    fetchUpstreamProfiles,
    updateUpstreamFormat,
  } from '$lib/api';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  type InsertableField = { label: string; sublabel: string; snippet: string };

  const CONTEXT_TOKENS: InsertableField[] = [
    { label: 'uid', sublabel: 'Device UID', snippet: '"uid": "{uid}"' },
    { label: 'deviceName', sublabel: "Device's name", snippet: '"deviceName": "{deviceName}"' },
    { label: 'groupName', sublabel: "Device's group name", snippet: '"groupName": "{groupName}"' },
    { label: 'timestamp', sublabel: 'When this record was sent', snippet: '"timestamp": "{timestamp}"' },
    { label: 'recordId', sublabel: 'Unique id per delivered message', snippet: '"recordId": "{recordId}"' },
  ];

  let kindTab = $state<UpstreamFormatKind>('sampling');
  let formats = $state<UpstreamFormatSummary[]>([]);
  let profiles = $state<Array<{ id: string; label: string }>>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let newName = $state('');
  let newProfileId = $state('');
  let creating = $state(false);
  let createError = $state<string | null>(null);

  let selectedId = $state<string | null>(null);
  let detail = $state<UpstreamFormatDetail | null>(null);
  let detailLoading = $state(false);
  let editName = $state('');
  let savingName = $state(false);
  let nameError = $state<string | null>(null);
  let nameOk = $state<string | null>(null);

  let busyDeleteId = $state<string | null>(null);
  let deleteError = $state<string | null>(null);

  // Template editor modal
  let modalOpen = $state(false);
  let draftTemplate = $state('');
  let templateError = $state<string | null>(null);
  let savingTemplate = $state(false);
  let textareaEl = $state<HTMLTextAreaElement | undefined>(undefined);

  const paramFields = $derived.by((): InsertableField[] => {
    if (!detail) return [];
    const out: InsertableField[] = [];
    for (const f of detail.availableFields) {
      out.push({
        label: f.jsonKey,
        sublabel: `${f.name}${f.unit ? ` (${f.unit})` : ''}`,
        snippet: `"${f.jsonKey}": "{param:${f.parameterId}}"`,
      });
      if (detail.kind === 'alarm' && f.alarmCapable) {
        out.push({
          label: `${f.jsonKey}_level`,
          sublabel: `${f.name} — alarm level`,
          snippet: `"${f.jsonKey}_level": "{alarmLevel:${f.parameterId}}"`,
        });
        out.push({
          label: `${f.jsonKey}_state`,
          sublabel: `${f.name} — alarm state`,
          snippet: `"${f.jsonKey}_state": "{alarmState:${f.parameterId}}"`,
        });
      }
    }
    return out;
  });

  async function load() {
    loading = true;
    error = null;
    try {
      const [fRes, pRes] = await Promise.all([fetchUpstreamFormats(kindTab), fetchUpstreamProfiles()]);
      formats = fRes.formats;
      profiles = pRes.profiles;
      if (!newProfileId) newProfileId = profiles[0]?.id ?? '';
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load formats';
    } finally {
      loading = false;
    }
  }

  async function switchTab(kind: UpstreamFormatKind) {
    if (kind === kindTab) return;
    kindTab = kind;
    selectedId = null;
    detail = null;
    await load();
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    createError = null;
    const name = newName.trim();
    if (!name || !newProfileId) {
      createError = 'Name and a source profile are required.';
      return;
    }
    creating = true;
    try {
      const res = await createUpstreamFormat({ kind: kindTab, name, profileRowId: newProfileId });
      formats = [...formats, res.format].sort((a, b) => a.name.localeCompare(b.name));
      newName = '';
      await selectFormat(res.format.id);
    } catch (err) {
      createError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      creating = false;
    }
  }

  async function selectFormat(id: string) {
    selectedId = id;
    detail = null;
    detailLoading = true;
    nameError = null;
    nameOk = null;
    try {
      const res = await fetchUpstreamFormat(id);
      detail = res.format;
      editName = res.format.name;
    } catch (err) {
      nameError = err instanceof ApiError ? err.message : 'Failed to load format';
    } finally {
      detailLoading = false;
    }
  }

  async function onSaveName() {
    if (!detail) return;
    nameError = null;
    nameOk = null;
    const name = editName.trim();
    if (!name) {
      nameError = 'Name is required.';
      return;
    }
    savingName = true;
    try {
      const res = await updateUpstreamFormat(detail.id, { name });
      detail = res.format;
      formats = formats.map((f) => (f.id === res.format.id ? res.format : f));
      nameOk = 'Saved.';
    } catch (err) {
      nameError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      savingName = false;
    }
  }

  async function onDelete(f: UpstreamFormatSummary) {
    if (!window.confirm(`Delete format "${f.name}"? Any group using it will stop delivering upstream.`)) {
      return;
    }
    deleteError = null;
    busyDeleteId = f.id;
    try {
      await deleteUpstreamFormat(f.id);
      formats = formats.filter((x) => x.id !== f.id);
      if (selectedId === f.id) {
        selectedId = null;
        detail = null;
      }
    } catch (err) {
      deleteError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      busyDeleteId = null;
    }
  }

  function openModal() {
    if (!detail) return;
    draftTemplate = detail.templateJson;
    templateError = null;
    modalOpen = true;
  }

  function closeModal() {
    modalOpen = false;
  }

  async function insertField(snippet: string) {
    const el = textareaEl;
    if (!el) return;
    const start = el.selectionStart ?? draftTemplate.length;
    const end = el.selectionEnd ?? draftTemplate.length;
    draftTemplate = draftTemplate.slice(0, start) + snippet + draftTemplate.slice(end);
    const cursor = start + snippet.length;
    await tick();
    el.focus();
    el.setSelectionRange(cursor, cursor);
  }

  async function onSaveTemplate() {
    if (!detail) return;
    templateError = null;
    try {
      JSON.parse(draftTemplate);
    } catch (err) {
      templateError = err instanceof Error ? `Invalid JSON: ${err.message}` : 'Invalid JSON';
      return;
    }
    savingTemplate = true;
    try {
      const res = await updateUpstreamFormat(detail.id, { templateJson: draftTemplate });
      detail = res.format;
      formats = formats.map((f) => (f.id === res.format.id ? res.format : f));
      modalOpen = false;
    } catch (err) {
      templateError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      savingTemplate = false;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Upstream Formats</h2>
  <p class="sub">
    Design the exact JSON your platform receives — any structure, any key names. Attach a format to
    a group at the group's page.
  </p>

  <div class="tabs">
    <button class="tab" class:active={kindTab === 'sampling'} onclick={() => void switchTab('sampling')}>
      Sampling
    </button>
    <button class="tab" class:active={kindTab === 'alarm'} onclick={() => void switchTab('alarm')}>
      Alarm
    </button>
  </div>

  {#if error}
    <Card><p class="err" role="alert">{error}</p></Card>
  {:else if loading}
    <Card><p class="muted">Loading…</p></Card>
  {:else}
    <div class="grid">
      <Card title={kindTab === 'sampling' ? 'Sampling formats' : 'Alarm formats'} padded={false}>
        {#if formats.length === 0}
          <p class="banner muted">No {kindTab} formats yet.</p>
        {:else}
          <ul class="list">
            {#each formats as f (f.id)}
              <li class:selected={selectedId === f.id}>
                <button class="row" onclick={() => void selectFormat(f.id)}>
                  <span class="name">{f.name}</span>
                  <span class="meta">{f.profileLabel} · {f.fieldCount} fields</span>
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busyDeleteId === f.id}
                  onclick={() => void onDelete(f)}
                >
                  {busyDeleteId === f.id ? '…' : 'Delete'}
                </Button>
              </li>
            {/each}
          </ul>
        {/if}
        {#if deleteError}
          <p class="err banner-pad" role="alert">{deleteError}</p>
        {/if}

        <form class="create" onsubmit={(e) => void onCreate(e)}>
          <label class="field">
            <span>New {kindTab} format name</span>
            <input type="text" bind:value={newName} maxlength="120" disabled={creating} required />
          </label>
          <label class="field">
            <span>Source profile</span>
            <select bind:value={newProfileId} disabled={creating || profiles.length === 0}>
              {#each profiles as p (p.id)}
                <option value={p.id}>{p.label}</option>
              {/each}
            </select>
          </label>
          <Button type="submit" disabled={creating || profiles.length === 0}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
          {#if profiles.length === 0}
            <p class="hint">No profile available yet — claim a device first.</p>
          {/if}
        </form>
        {#if createError}
          <p class="err banner-pad" role="alert">{createError}</p>
        {/if}
      </Card>

      <Card title="Format" subtitle={detail ? detail.profileLabel : undefined}>
        {#if !selectedId}
          <p class="muted">Select a format to view and edit its output JSON.</p>
        {:else if detailLoading}
          <p class="muted">Loading…</p>
        {:else if detail}
          <label class="field">
            <span>Name</span>
            <div class="name-row">
              <input type="text" bind:value={editName} maxlength="120" disabled={savingName} required />
              <Button variant="secondary" disabled={savingName || editName.trim() === detail.name} onclick={() => void onSaveName()}>
                {savingName ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </label>
          {#if nameError}
            <p class="err" role="alert">{nameError}</p>
          {/if}
          {#if nameOk}
            <p class="ok" role="status">{nameOk}</p>
          {/if}

          <div class="template-preview">
            <div class="preview-head">
              <span>Output JSON template ({detail.fieldCount} fields)</span>
              <Button variant="primary" size="sm" onclick={openModal}>Edit template</Button>
            </div>
            <pre class="preview-body">{detail.templateJson}</pre>
          </div>
        {/if}
      </Card>
    </div>
  {/if}
</div>

{#if modalOpen && detail}
  <div class="modal-backdrop" role="presentation" onclick={closeModal}>
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Edit output JSON template"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => {
        if (e.key === 'Escape') closeModal();
      }}
    >
      <header class="modal-head">
        <div>
          <h3>{detail.name}</h3>
          <p class="modal-sub">{detail.kind === 'sampling' ? 'Sampling' : 'Alarm'} · {detail.profileLabel}</p>
        </div>
        <button class="modal-close" onclick={closeModal} aria-label="Close">×</button>
      </header>
      <div class="modal-body">
        <aside class="field-picker">
          <p class="picker-label">Insert field at cursor</p>
          <div class="picker-list">
            <p class="picker-group">Device parameters</p>
            {#each paramFields as f (f.snippet)}
              <button type="button" class="picker-item" onclick={() => void insertField(f.snippet)}>
                <span class="picker-name">{f.label}</span>
                <span class="picker-sub">{f.sublabel}</span>
              </button>
            {/each}
            <p class="picker-group">Context tokens</p>
            {#each CONTEXT_TOKENS as f (f.snippet)}
              <button type="button" class="picker-item" onclick={() => void insertField(f.snippet)}>
                <span class="picker-name">{f.label}</span>
                <span class="picker-sub">{f.sublabel}</span>
              </button>
            {/each}
          </div>
        </aside>
        <div class="editor-pane">
          <textarea
            class="template-editor"
            bind:value={draftTemplate}
            bind:this={textareaEl}
            spellcheck="false"
            disabled={savingTemplate}
          ></textarea>
          {#if templateError}
            <p class="err" role="alert">{templateError}</p>
          {/if}
        </div>
      </div>
      <footer class="modal-foot">
        <Button variant="ghost" disabled={savingTemplate} onclick={closeModal}>Cancel</Button>
        <Button variant="primary" disabled={savingTemplate} onclick={() => void onSaveTemplate()}>
          {savingTemplate ? 'Saving…' : 'Save template'}
        </Button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 72rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
    max-width: 52rem;
  }
  .tabs {
    display: flex;
    gap: 0.5rem;
  }
  .tab {
    font: inherit;
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-muted);
    cursor: pointer;
  }
  .tab.active {
    background: var(--accent, #2563eb);
    color: white;
    border-color: transparent;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 1rem;
    align-items: start;
  }
  @media (max-width: 900px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
  .muted {
    color: var(--text-muted);
  }
  .err {
    color: #f87171;
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .ok {
    color: var(--success, #22c55e);
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .banner.muted {
    padding: 1rem;
    margin: 0;
  }
  .banner-pad {
    padding: 0 1rem 0.75rem;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .list li.selected {
    background: var(--surface-2, #f9fafb);
  }
  .row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
    flex: 1;
    min-width: 0;
  }
  .name {
    font-weight: 600;
  }
  .meta {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .create {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 1rem;
    border-top: 1px solid var(--border);
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.9rem;
  }
  .field span {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .field input,
  .field select {
    font: inherit;
    padding: 0.45rem 0.6rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: inherit;
  }
  .name-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .name-row input {
    flex: 1;
  }
  .hint {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .template-preview {
    margin-top: 1rem;
  }
  .preview-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
  .preview-body {
    margin: 0;
    padding: 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #0b0b0f);
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.82rem;
    max-height: 20rem;
    overflow: auto;
    white-space: pre;
  }

  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: color-mix(in srgb, black 55%, transparent);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 1.5rem;
  }
  .modal {
    background: var(--surface, #fff);
    border-radius: 0.75rem;
    border: 1px solid var(--border);
    width: min(72rem, 100%);
    height: min(42rem, 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  }
  .modal-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border);
  }
  .modal-head h3 {
    margin: 0;
    font-size: 1.1rem;
  }
  .modal-sub {
    margin: 0.2rem 0 0;
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .modal-close {
    font: inherit;
    font-size: 1.3rem;
    line-height: 1;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.2rem 0.4rem;
  }
  .modal-close:hover {
    color: var(--text);
  }
  .modal-body {
    flex: 1;
    display: grid;
    grid-template-columns: 16rem 1fr;
    min-height: 0;
  }
  .field-picker {
    border-right: 1px solid var(--border);
    overflow-y: auto;
    padding: 0.75rem;
  }
  .picker-label {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .picker-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .picker-group {
    margin: 0.6rem 0 0.1rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
  }
  .picker-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.1rem;
    text-align: left;
    font: inherit;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.4rem;
    padding: 0.35rem 0.5rem;
    cursor: pointer;
    color: inherit;
  }
  .picker-item:hover {
    background: var(--surface-2, #f3f4f6);
    border-color: var(--border);
  }
  .picker-name {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.8rem;
    font-weight: 600;
  }
  .picker-sub {
    font-size: 0.72rem;
    color: var(--text-muted);
  }
  .editor-pane {
    display: flex;
    flex-direction: column;
    padding: 0.75rem;
    min-height: 0;
  }
  .template-editor {
    flex: 1;
    width: 100%;
    resize: none;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85rem;
    line-height: 1.5;
    padding: 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #0b0b0f);
    color: inherit;
  }
  .modal-foot {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.85rem 1.25rem;
    border-top: 1px solid var(--border);
  }
</style>
